/*
  Copyright 2018-2020 National Geographic Society

  Use of this software does not constitute endorsement by National Geographic
  Society (NGS). The NGS name and NGS logo may not be used for any purpose without
  written permission from NGS.

  Licensed under the Apache License, Version 2.0 (the "License"); you may not use
  this file except in compliance with the License. You may obtain a copy of the
  License at

      https://www.apache.org/licenses/LICENSE-2.0

  Unless required by applicable law or agreed to in writing, software distributed
  under the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR
  CONDITIONS OF ANY KIND, either express or implied. See the License for the
  specific language governing permissions and limitations under the License.
*/

import React, { Children, cloneElement } from 'react';
import { noop } from 'lodash';

import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import Badge from '@material-ui/core/Badge';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import MoreHorizIcon from '@material-ui/icons/MoreHoriz'; // Import the horizonatal kebab icon
import IconButton from '@material-ui/core/IconButton';
import IconClose from 'mdi-material-ui/Close';
//import IconMenu from 'mdi-material-ui/Menu';
import MoreVertIcon from '@material-ui/icons/MoreVert'; // Import the kebab icon
//import PendingOutlinedIcon from '@mui/icons-material/PendingOutlined';
import DotsVerticalCircleOutline from 'mdi-material-ui/DotsVerticalCircleOutline';
import Menu from '@material-ui/core/Menu';
import ToggleIcon from 'material-ui-toggle-icon';
import Link from 'redux-first-router-link';

import { Option } from './Option';
import { SessionStorage } from '@marapp/earth-shared';

interface IProps {
  label: string;
  logo?: React.ReactNode;
  value?: any;
  checkedCount?: number;
  renderButton?: boolean;
  renderDropdown?: boolean;
  children?: React.ReactNode | React.ReactNodeArray;
  onChange?: (value?: any) => any;
  onClose?: () => void;
  handleResetLocation?: (value?: any) => any;
}

const useStyles = makeStyles((theme) => {
  return {
    root: {
      backgroundColor: theme.palette.grey['600'],
    },
    logo: {
      '& img': {
        maxHeight: theme.spacing(6),
        width: 'auto',
        marginBottom: theme.spacing(6),
      },
    },
    menuRoot: {
      paddingLeft: '0px !important',
      minWidth: 280,
      maxWidth: 345,
    },
    menuPopOver: {
      // background: 'rgba(0,0,0,0.5)', // Darkens the backdrop when the menu is open
    },
  };
});

const AppContextSwitcher = (props: IProps) => {
  const {
    label,
    logo,
    value,
    checkedCount = 0,
    renderButton = false,
    renderDropdown = true,
    children,
    onChange = noop,
    handleResetLocation,
  } = props;

  const { t, i18n } = useTranslation();

  const classes = useStyles();

  const [anchorEl, setAnchorEl] = React.useState(null);

  const toggleDropdown = (event) => setAnchorEl(event.currentTarget);

  const closeDropdown = () => setAnchorEl(null);

  const isOpen = !!anchorEl;

  return (
    <Box className={classes.root}>
      <Grid
        // container={true}
        container
        className="marapp-qa-context-switcher"
        justifyContent="space-between"
        alignItems="center"
        wrap="nowrap"
        // spacing={2}
      >
        <Grid item className={`${classes.logo} marapp-qa-logo`} onClick={closeDropdown}>
          {logo}
        </Grid>

        <Grid item>
          {renderDropdown && (
            <>
              <IconButton
                color="primary"
                onClick={toggleDropdown}
                size="small" // or "medium"
                aria-label="Open workspaces selector"
                style={{ height: 36, width: 36, borderRadius: 8 }}
              >
                <ToggleIcon
                  on={isOpen}
                  onIcon={<IconClose />}
                  offIcon={<MoreHorizIcon />}
                  //offIcon={<DotsVerticalCircleOutline />}
                />
              </IconButton>

              <Menu
                open={isOpen}
                anchorEl={anchorEl}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                marginThreshold={48} // hack to push the menu below the trigger button
                getContentAnchorEl={null}
                onClose={closeDropdown}
                className="marapp-qa-dropdown marapp-qa-menu-dropdown"
                PopoverClasses={{
                  root: classes.menuPopOver,
                }}
                classes={{
                  list: classes.menuRoot,
                }}
              >
                {Children.map(children, (child: any) => {
                  if (!child) {
                    return;
                  }
                  const isOptionElement = child.props.value;
                  const selected = child.props.value === value;
                  return isOptionElement
                    ? cloneElement(child, {
                        ...child.props,
                        selected,
                        onClick: (value: any) => {
                          if (!selected) {
                            onChange(value);
                          }
                          closeDropdown();
                        },
                      })
                    : child;
                })}
              </Menu>
            </>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

// Child components
AppContextSwitcher.Option = Option;

export { AppContextSwitcher };
